import {
  buildRawGpsGeometry, distanceMeters, EDGE_CLASSIFICATIONS, RAW_GPS_GEOMETRY_PROVIDER,
} from "../utils/gpsReplayGeometry";

const OSRM_GEOMETRY_PROVIDER = "osrm";

function nearestCoordinateIndex(coordinates, target, minimumIndex) {
  let selected = minimumIndex;
  let minimumDistance = Infinity;
  for (let index = minimumIndex; index < coordinates.length; index += 1) {
    const distance = distanceMeters(coordinates[index], target);
    if (distance < minimumDistance) {
      minimumDistance = distance;
      selected = index;
    }
  }
  return selected;
}

function timedPath(coordinates, fromObservation, toObservation) {
  const cleaned = coordinates.filter((point, index) => index === 0 ||
    point.latitude !== coordinates[index - 1].latitude || point.longitude !== coordinates[index - 1].longitude
  );
  if (cleaned.length < 2) return null;
  const cumulative = [0];
  for (let index = 1; index < cleaned.length; index += 1) {
    cumulative.push(cumulative.at(-1) + distanceMeters(cleaned[index - 1], cleaned[index]));
  }
  const total = cumulative.at(-1);
  const fromMs = fromObservation.recordedAt.getTime();
  const toMs = toObservation.recordedAt.getTime();
  if (!(toMs > fromMs) || !(total > 0)) return null;
  return Object.freeze(cleaned.map((coordinatesValue, index) => Object.freeze({
    coordinates: Object.freeze({ ...coordinatesValue }),
    recordedAt: new Date(fromMs + (toMs - fromMs) * cumulative[index] / total),
  })));
}

function buildMatchedGeometry(observations, contract) {
  if (contract?.status !== "matched" || contract.geometryProvider !== OSRM_GEOMETRY_PROVIDER) {
    throw new TypeError("Matched replay geometry is unavailable.");
  }
  const raw = buildRawGpsGeometry(observations);
  const matchedEdgePaths = Array(observations.length - 1).fill(null);
  const matchedObservationCoordinates = Array(observations.length).fill(null);
  const segments = [];
  contract.matchedSegments.forEach((segment, segmentIndex) => {
    const anchors = contract.sourceObservationAnchors
      .filter((anchor) => anchor.matchedSegmentIndex === segmentIndex)
      .sort((left, right) => left.sourceObservationIndex - right.sourceObservationIndex);
    let minimumCoordinateIndex = 0;
    const indexedAnchors = anchors.map((anchor) => {
      const coordinateIndex = nearestCoordinateIndex(
        segment.coordinates, anchor.matchedCoordinates, minimumCoordinateIndex
      );
      minimumCoordinateIndex = coordinateIndex;
      return { ...anchor, coordinateIndex };
    });
    indexedAnchors.forEach((anchor) => {
      matchedObservationCoordinates[anchor.sourceObservationIndex] = anchor.matchedCoordinates;
    });
    for (let index = 0; index < indexedAnchors.length - 1; index += 1) {
      const fromAnchor = indexedAnchors[index];
      const toAnchor = indexedAnchors[index + 1];
      const fromIndex = fromAnchor.sourceObservationIndex;
      const toIndex = toAnchor.sourceObservationIndex;
      if (toIndex !== fromIndex + 1 ||
          raw.edges[fromIndex]?.classification !== EDGE_CLASSIFICATIONS.RELIABLE_MOVEMENT ||
          toAnchor.coordinateIndex < fromAnchor.coordinateIndex) continue;
      const middle = segment.coordinates.slice(fromAnchor.coordinateIndex + 1, toAnchor.coordinateIndex);
      const points = timedPath([
        fromAnchor.matchedCoordinates, ...middle, toAnchor.matchedCoordinates,
      ], observations[fromIndex], observations[toIndex]);
      if (!points) continue;
      matchedEdgePaths[fromIndex] = points;
      segments.push(Object.freeze({
        id: `osrm-edge-${fromIndex}`,
        provider: OSRM_GEOMETRY_PROVIDER,
        points,
      }));
    }
  });
  const reliableIndexes = raw.edges
    .filter((edge) => edge.classification === EDGE_CLASSIFICATIONS.RELIABLE_MOVEMENT)
    .map((edge) => edge.fromIndex);
  if (!reliableIndexes.length || reliableIndexes.some((index) => !matchedEdgePaths[index])) {
    throw new TypeError("Matched replay geometry does not cover every reliable edge.");
  }
  return Object.freeze({
    ...raw,
    provider: OSRM_GEOMETRY_PROVIDER,
    providerVersion: contract.providerVersion,
    geometryVersion: contract.geometryVersion,
    segments: Object.freeze(segments),
    matchedEdgePaths: Object.freeze(matchedEdgePaths),
    matchedObservationCoordinates: Object.freeze(matchedObservationCoordinates),
    matchedRoadDistanceMeters: contract.matchedRoadDistanceMeters,
    observedGpsDistanceMeters: contract.observedGpsDistanceMeters,
  });
}

function geometryPreservesTimeline(geometry, observations) {
  if (!geometry || !Array.isArray(geometry.observations) ||
      geometry.observations.length !== observations.length ||
      !Array.isArray(geometry.segments) || !Array.isArray(geometry.edges) ||
      !Array.isArray(geometry.gaps) || !Array.isArray(geometry.dwells)) return false;
  return geometry.observations.every((observation, index) =>
    observation?.recordedAt instanceof Date &&
    observation.recordedAt.getTime() === observations[index].recordedAt.getTime()
  );
}

export const rawGpsReplayGeometryProvider = Object.freeze({
  id: RAW_GPS_GEOMETRY_PROVIDER,
  build({ observations }) {
    return buildRawGpsGeometry(observations);
  },
});

export function createBackendReplayGeometryProvider(contract) {
  return Object.freeze({
    id: contract?.requestedProvider || OSRM_GEOMETRY_PROVIDER,
    build({ observations }) {
      return buildMatchedGeometry(observations, contract);
    },
  });
}

export function resolveReplayGeometry({ snapshot, provider = rawGpsReplayGeometryProvider } = {}) {
  if (!snapshot || !Array.isArray(snapshot.observations)) return null;
  const requestedProvider = typeof provider?.id === "string" ? provider.id : "invalid";
  if (typeof provider?.build === "function") {
    try {
      const geometry = provider.build({
        observations: snapshot.observations,
        range: snapshot.range,
        source: snapshot.source,
      });
      if (geometryPreservesTimeline(geometry, snapshot.observations)) {
        return Object.freeze({
          geometry,
          requestedProvider,
          activeProvider: geometry.provider || requestedProvider,
          fallback: false,
          fallbackReason: null,
        });
      }
    } catch {
      // A derived provider must never prevent raw replay from loading.
    }
  }
  const geometry = rawGpsReplayGeometryProvider.build({ observations: snapshot.observations });
  return Object.freeze({
    geometry,
    requestedProvider,
    activeProvider: RAW_GPS_GEOMETRY_PROVIDER,
    fallback: true,
    fallbackReason: "derived_geometry_unavailable",
  });
}

export { RAW_GPS_GEOMETRY_PROVIDER };
